<template>
    <div class="field-editor">
        <input :id="props.id" type="text" class="sr-only" :tabindex="props.tabindex" @focusin="handleLabelFocus" />

        <!-- Fields -->
        <Sortable
            :list="internalFields"
            item-key="_id"
            tag="div"
            :group="field.group ?? 'fields'"
            class="field-editor__sortable"
            @end="handleSortEnd"
            @add="handleAdd"
        >
            <template #empty>
                <div class="field-editor__empty">
                    {{ lang('fields::fields.repeater.empty') }}
                </div>
            </template>
            <template #item="{ element }">
                <FieldPanel
                    :ref="
                        (el) => {
                            if (el) panelRefs[getGlobalIndex(element)] = el;
                        }
                    "
                    :field="element"
                    :index="getGlobalIndex(element)"
                    :types="types"
                    :available-fields="availableFieldsForConditions"
                    :sibling-field-slugs="getSiblingFieldSlugs(element._id)"
                    @update:field="updateField(element._id, $event)"
                    @remove="removeField(element._id)"
                />
            </template>
        </Sortable>

        <!-- Footer Actions -->
        <div class="field-editor__actions">
            <Button type="button" size="sm" @click="addField">
                <icon icon="hero/solid/plus-sm" class="w-4 h-4 mr-1" />
                {{ lang('fields::fields.field-editor.add_field') }}
            </Button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { v4 as uuidv4 } from 'uuid';
import { arrayMove } from '@src/Common/Helpers/array';
import Sortable from '@src/Common/Components/Sortable.vue';
import FieldPanel from './FieldPanel.vue';
import type { FieldComponent } from '@src/Common/Components/types';

interface FieldData {
    _id?: string;
    slug: string;
    name: string;
    type: string;
    required?: boolean;
    multiple?: boolean;
    display?: boolean;
    labelPosition?: string;
    tooltip?: string;
    has_tooltip?: boolean;
    foreign_id?: string;
    has_foreign_id?: boolean;
    mask?: string;
    options?: any[];
    displayRepeaterHeader?: boolean;
    has_tenancy?: boolean;
    validation?: Record<string, any>;
    conditions?: Record<string, any>;
    has_condition?: boolean;
    width?: string | null;
    placeholder?: string;
    help?: string;
    wrapperClass?: string;
    [key: string]: any;
}

const modelValue = defineModel<FieldData[]>({
    type: Array,
    default: () => [],
});

const props = withDefaults(defineProps<FieldComponent>(), {
    field: () => ({
        name: '',
        slug: '',
        type: 'field-editor',
    }),
    tabindex: null,
});

const $page = usePage();

// Types from page props
const allTypes = computed<{ id: string; name: string; admin?: Record<string, any> }[]>(
    () => ($page.props as any).types ?? [],
);

// Filter types based on allowedTypes field option
const types = computed(() => {
    const allowedTypes = props.field.allowedTypes as string[] | undefined;
    if (!allowedTypes || allowedTypes.length === 0) {
        return allTypes.value;
    }
    return allTypes.value.filter((t) => allowedTypes.includes(t.id));
});

// Internal state with _id for drag/drop
const internalFields = ref<FieldData[]>([]);
const panelRefs = ref<Record<number, any>>({});
const isUpdatingFromExternal = ref(false);

// Last emitted value for comparison (avoid unnecessary updates)
let lastEmittedJson = '';

// Initialize from modelValue
const initializeFields = () => {
    isUpdatingFromExternal.value = true;

    if (!modelValue.value || !Array.isArray(modelValue.value)) {
        internalFields.value = [];
        lastEmittedJson = '[]';
        isUpdatingFromExternal.value = false;
        return;
    }

    internalFields.value = modelValue.value.map((item) => ({
        ...item,
        _id: item._id ?? uuidv4(),
        conditions: item.conditions ?? (item.has_condition ? [{ field: '', comparison: '==', value: '' }] : {}),
    }));

    // Set initial comparison value to avoid immediate re-emit
    lastEmittedJson = JSON.stringify(
        internalFields.value.map((item) => {
            const clean = { ...item };
            delete clean._id;
            return clean;
        }),
    );

    nextTick(() => {
        isUpdatingFromExternal.value = false;
    });
};

initializeFields();

const getGlobalIndex = (element: FieldData) => internalFields.value.findIndex((f) => f._id === element._id);

// Available fields for conditions (excluding current field)
const availableFieldsForConditions = computed(() =>
    internalFields.value.map((f) => ({
        slug: f.slug,
        name: f.name || f.slug,
    })),
);

// Get sibling field slugs for uniqueness validation (excluding given field)
const getSiblingFieldSlugs = (excludeId: string): string[] => {
    return internalFields.value
        .filter((f) => f._id !== excludeId && f.slug)
        .map((f) => f.slug);
};

// Emit updates
const emitUpdate = () => {
    // Skip if we're updating from external source to avoid infinite loop
    if (isUpdatingFromExternal.value) return;

    const cleanedFields = internalFields.value.map((item) => {
        const clean = { ...item };
        delete clean._id;
        return clean;
    });

    // Compare with last emitted to avoid unnecessary updates
    const newJson = JSON.stringify(cleanedFields);
    if (newJson === lastEmittedJson) return;

    lastEmittedJson = newJson;
    modelValue.value = cleanedFields;
};

// Watch internal changes
watch(internalFields, emitUpdate, { deep: true });

// Field operations
const addField = async () => {
    // Use 'text' as default, or first available type if 'text' is not allowed
    const defaultType = types.value.find((t) => t.id === 'text')?.id ?? types.value[0]?.id ?? 'text';

    const newField: FieldData = {
        _id: uuidv4(),
        slug: '',
        name: '',
        type: defaultType,
        required: false,
        multiple: false,
        display: false,
        labelPosition: 'left',
        conditions: {},
    };

    internalFields.value.push(newField);
    await nextTick();

    // Expand the new field
    const index = internalFields.value.length - 1;
    panelRefs.value[index]?.expand();
};

const updateField = (fieldId: string, updatedField: FieldData) => {
    const index = internalFields.value.findIndex((f) => f._id === fieldId);
    if (index !== -1) {
        internalFields.value[index] = { ...updatedField, _id: fieldId };
    }
};

const removeField = (fieldId: string) => {
    const index = internalFields.value.findIndex((f) => f._id === fieldId);
    if (index !== -1) {
        internalFields.value.splice(index, 1);
    }
};

// Sorting
const handleSortEnd = (event: any) => {
    if (event.from === event.to) {
        // Same list reorder
        const content = arrayMove([...internalFields.value], event.oldIndex, event.newIndex);
        internalFields.value = content;
    } else {
        // Item was dragged out of this list to another
        const content = [...internalFields.value];
        content.splice(event.oldIndex, 1);
        internalFields.value = content;
    }
};

const handleAdd = (event: any, item: any) => {
    const content = [...internalFields.value];

    if (!item._id) {
        item._id = uuidv4();
    }

    content.splice(event.newIndex, 0, item);
    internalFields.value = content;

    // Remove DOM element SortableJS inserted - Vue reactivity handles rendering
    event.item.parentNode?.removeChild(event.item);
};

// Focus handling
const handleLabelFocus = async () => {
    if (internalFields.value.length > 0) {
        panelRefs.value[0]?.expand();
    } else {
        await addField();
    }
};
</script>
